import { TZDate } from '@repo/tzdate';
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import BookingBase from 'src/components/domain/booking-base/BookingBase';
import BookingToggleOpenButton from 'src/components/domain/booking-widget-card/BookingToggleOpenButton';
import { useOnClickBookPackage } from 'src/hooks/domain/activities/useOnClickBookPackage';
import { useWidgetEventEffect } from 'src/hooks/domain/events/useWidgetEventEffect';
import { usePackageQuantities } from 'src/hooks/domain/booking/usePackageQuantities';
import { useLocale } from '@repo/i18n';
import { PackageTicketOptionWithQuantity } from '@repo/types';
import { capitalize } from '@repo/common-utils/TextUtils';
import {
    usePackageProduct,
    usePackageAvailability,
    useNextPackageAvailability,
} from '@repo/widget-utils/services/hooks/product';
import { groupBy } from 'lodash-es';
import { useBookingContext } from 'src/widgets/BookingContext';
import { setQuantities, setDefaultTicketOptions } from 'src/state/booking/bookingReducer';
import useSummaryPriceLines from 'src/hooks/domain/useSummaryPriceLines';
import { usePricePropsFromQuantitiesInBookingContext } from 'src/components/domain/booking-widget-card/booking-header/BookingHeader';

interface IProps {
    packageId: string;
    expandArrowInside: boolean;
    positionOffscreen?: boolean;
    dateSelectionFirst?: boolean;
}

export default function PackageBooking(props: IProps): JSX.Element {
    const {
        packageId,
        expandArrowInside,
        positionOffscreen = false,
        dateSelectionFirst = false,
    } = props;

    const {
        visible,
        quantities,
        bookingDispatch,
        hasChosenDate,
        isLoadingPrices,
        selectedTicketType,
        attemptedBooking,
    } = useBookingContext<PackageTicketOptionWithQuantity>();
    const { t } = useLocale();

    const { data: pkg = null, isLoading: isLoadingPkg } = usePackageProduct(packageId);
    const { data: nextPackageAvailability, isLoading: isLoadingNextAvailability } =
        useNextPackageAvailability(packageId);

    useEffect(() => {
        bookingDispatch(setQuantities(pkg?.ticketOptions ?? []));
    }, [pkg, bookingDispatch]);

    // Set default ticket options when package loads (for showing unavailable tickets)
    useEffect(() => {
        if (pkg?.ticketOptions) {
            bookingDispatch(setDefaultTicketOptions(pkg.ticketOptions));
        }
    }, [pkg?.ticketOptions, bookingDispatch]);

    const [availabilitySearchPeriod, setAvailabilitySearchPeriod] = useState<{
        startDay: TZDate | null;
        endDay: TZDate | null;
    }>({
        startDay: null,
        endDay: null,
    });

    useEffect(() => {
        if (nextPackageAvailability === undefined) return; // still loading

        if (nextPackageAvailability?.next) {
            const nextDate = new TZDate(nextPackageAvailability.next);
            setAvailabilitySearchPeriod({
                startDay: nextDate.startOf('month'),
                endDay: nextDate.endOf('month'),
            });
        } else {
            // No availability found, fall back to current month
            setAvailabilitySearchPeriod({
                startDay: TZDate.now().startOf('month'),
                endDay: TZDate.now().endOf('month'),
            });
        }
    }, [nextPackageAvailability]);

    const [selectedDate, setSelectedDate] = useState<TZDate | null>(null);
    const onSelectDate = useCallback((date: TZDate | null) => {
        // Packages always start at the beginning of the day, so we need to set the time to 00:00:00
        setSelectedDate(date?.startOf('day') ?? null);
    }, []);

    const { data: pkgAvailability, isLoading: isLoadingPkgAvailability } = usePackageAvailability(
        availabilitySearchPeriod.startDay ? pkg : null,
        availabilitySearchPeriod.startDay ?? TZDate.now(),
        availabilitySearchPeriod.endDay ?? TZDate.now().add(1, 'month'),
    );

    // Loading if either package metadata, next availability, OR availability is loading
    const isLoadingAvailabilities =
        isLoadingPkg || isLoadingNextAvailability || isLoadingPkgAvailability;

    const pkgAvailabilityWithQuantitiesCheck = useMemo(
        () =>
            Object.values(groupBy(pkgAvailability, (p) => p.start.format('YYYY-MM-DD'))).flatMap(
                (availabilityDay) => {
                    const isAvailable = quantities.every(
                        (q) =>
                            q.quantity === 0 ||
                            availabilityDay.some((a) =>
                                a.ticketOptions.some(
                                    (to) => to.ticketCategoryId === q.ticketCategoryId,
                                ),
                            ),
                    );
                    if (isAvailable) return availabilityDay;
                    else return [];
                },
            ),
        [pkgAvailability, quantities],
    );

    useWidgetEventEffect(
        (pkg) => ({
            eventType: 'viewItem',
            product: pkg,
            productType: 'Package',
        }),
        pkg,
        visible,
    );

    const packagePrices = useMemo(() => (pkg ? pkg.ticketOptions : []), [pkg]);
    const packageFromPrice = Math.min(...packagePrices.map((p) => p.price));

    // Enrich quantities with productInstances from availability data
    const priceQuantities = useMemo(() => {
        if (!quantities || !pkgAvailability || pkgAvailability.length === 0) {
            return quantities;
        }

        return quantities.map((q) => ({
            ...q,
            productInstances: pkgAvailability?.filter((p) => p.product?.pkgTicketOptionId === q.id),
        }));
    }, [quantities, pkgAvailability]);

    const onClickBook = useOnClickBookPackage(pkg, packagePrices, selectedDate, onSelectDate);

    const quantitiesForSummary = hasChosenDate ? priceQuantities : [];
    const isDetailedLines = false;
    const summaryLines = useSummaryPriceLines(
        quantitiesForSummary,
        isDetailedLines,
        isLoadingPrices,
        selectedTicketType,
    );

    // Use new domain hook for package quantities (pass enriched quantities)
    const { inputItems, errors, onChangeQuantity, disabledMessage } =
        usePackageQuantities(priceQuantities);

    // Split errors for BookingBase compatibility
    const quantityErrors = errors.filter((e) => e.type === 'individual');
    const totalErrors = errors.filter((e) => e.type === 'total');

    // Calculate button state
    const disableBookButton = (!hasChosenDate && attemptedBooking) || errors.length > 0;

    // Package-specific warning label logic
    function getWarningLabel() {
        if (!hasChosenDate) return undefined;
        if (errors.some((e) => e.error === 'minEntrants')) {
            return t.a_minimum_of_participants_is_required_to_book_this_product;
        }
        if (errors.some((e) => e.error === 'capacity')) {
            return t.one_or_more_activities_or_accommodations_dont_have_enough_capacity;
        }
        return undefined;
    }
    const warningLabel = getWarningLabel();

    const bookingHeaderPriceProps = usePricePropsFromQuantitiesInBookingContext(
        priceQuantities,
        packageFromPrice,
    );

    return (
        <BookingBase
            isPackageCalendar
            hideLeftButton
            productTitle={pkg?.title ?? ''}
            travelerQuantities={priceQuantities}
            availabilityData={pkgAvailabilityWithQuantitiesCheck}
            rightButtonLabel={capitalize(t.book_now)}
            onClickRightButton={onClickBook}
            availabilitySearchPeriod={availabilitySearchPeriod}
            setAvailabilitySearchPeriod={setAvailabilitySearchPeriod}
            selectedDate={selectedDate}
            setSelectedDate={onSelectDate}
            title={capitalize(t.book_now)}
            expandArrowInside={expandArrowInside}
            toggleButton={
                <BookingToggleOpenButton expandArrowInside={expandArrowInside} visible={visible} />
            }
            positionOffscreen={positionOffscreen}
            isLoadingAvailabilities={isLoadingAvailabilities}
            dateSelectionFirst={dateSelectionFirst}
            summaryLines={summaryLines}
            disableBookButton={disableBookButton}
            quantityErrors={quantityErrors}
            totalErrors={totalErrors}
            warningLabel={warningLabel}
            quantityInputItems={inputItems}
            onChangeQuantity={onChangeQuantity}
            bookingHeaderPriceProps={bookingHeaderPriceProps}
            disabledMessage={disabledMessage}
        />
    );
}
